/**
 * AdminRail.tsx
 *
 * EMS Admin Navigation Rail - Category-Centric Deep Hierarchy
 * Structure: Top Category > Submenu > Vendor > Menu > Submenu (if needed)
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  Users,
  Settings,
  Shield,
  Database,
  Activity,
  BarChart3,
  FileText,
  Puzzle,
  Webhook,
  Terminal,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Command,
  Layers,
  Zap,
  Server,
  HardDrive,
  MonitorPlay,
  Cloud,
  Search,
  Box,
  Wrench,
  Network,
  UserCog,
  Monitor,
  FolderOpen,
  Router,
  Cpu,
  Container,
  HardDriveDownload,
  ShieldCheck,
  Bug,
  Package,
  RefreshCw,
  Archive,
  AppWindow,
  Key,
  Mail,
  ClipboardCheck,
  Sparkles,
  Code,
  GitBranch,
  FileCode,
  Building2,
  LifeBuoy,
  AlertCircle,
  Wifi,
  Eye,
  Scan,
  UserPlus,
  ScrollText,
  Fingerprint,
  Workflow,
  FolderKanban,
  ShoppingBag,
  Rocket,
  Ship,
  FolderTree,
  HeartPulse,
  DollarSign,
  UserCheck,
  CloudCog,
  KeyRound,
  FileSearch,
  History,
  Save,
} from 'lucide-react';
import { useLayout } from '@/base/contexts/LayoutContext';
import { AvatarMenu } from './AvatarMenu';
import { EmergencyLockButton } from './EmergencyLockButton';
import { fetchEnabledModules, getSetupMode, type EnabledModule } from '@/base/utils/api';

// ============================================================================
// Types
// ============================================================================

interface NavItem {
  to: string;
  icon: React.ComponentType<{ size?: number; className?: string }>;
  label: string;
  badge?: string | number;
}

interface VendorGroup {
  id: string;
  label: string;
  icon: React.ComponentType<{ size?: number; className?: string }>;
  color: string;
  items: NavItem[];
  // For modules that require specific module IDs to be enabled
  requiredModules?: string[];
}

interface SubMenu {
  id: string;
  label: string;
  icon: React.ComponentType<{ size?: number; className?: string }>;
  vendors: VendorGroup[];
}

interface TopCategory {
  id: string;
  label: string;
  icon: React.ComponentType<{ size?: number; className?: string }>;
  color: string;
  // Direct items (for categories without submenus)
  items?: NavItem[];
  // Submenus with vendor groupings
  subMenus?: SubMenu[];
}

// ============================================================================
// Navigation Configuration
// Top Category > Submenu > Vendor > Menu > Submenu
// ============================================================================

const navigationConfig: TopCategory[] = [
  // ==========================================================================
  // OVERVIEW
  // ==========================================================================
  {
    id: 'overview',
    label: 'Overview',
    icon: LayoutDashboard,
    color: 'var(--ems-primary)',
    items: [
      { to: '/', icon: LayoutDashboard, label: 'Dashboard' },
      { to: '/activity', icon: Activity, label: 'Activity Feed' },
    ],
  },

  // ==========================================================================
  // INFRASTRUCTURE
  // ==========================================================================
  {
    id: 'infrastructure',
    label: 'Infrastructure',
    icon: Server,
    color: '#3B82F6',
    subMenus: [
      {
        id: 'compute',
        label: 'Compute',
        icon: Cpu,
        vendors: [
          {
            id: 'vmware-compute',
            label: 'VMware',
            icon: Cloud,
            color: '#607D8B',
            requiredModules: ['vmware-esxi', 'vmware-vcsa', 'vmware-sddc', 'vmware-hcx'],
            items: [
              { to: '/vmware-esxi', icon: Server, label: 'ESXi Hosts' },
              { to: '/vmware-vcsa', icon: Cloud, label: 'vCenter' },
              { to: '/vmware-sddc', icon: Layers, label: 'SDDC Manager' },
              { to: '/vmware-sddc/domains', icon: Cloud, label: 'Domains' },
              { to: '/vmware-sddc/hosts', icon: Server, label: 'SDDC Hosts' },
              { to: '/vmware-hcx', icon: RefreshCw, label: 'HCX Migration' },
              { to: '/vmware-hcx/migrations', icon: RefreshCw, label: 'Migrations' },
              { to: '/vmware-hcx/sites', icon: Network, label: 'Site Pairs' },
              { to: '/vmware-hcx/service-mesh', icon: Layers, label: 'Service Mesh' },
            ],
          },
          {
            id: 'nutanix',
            label: 'Nutanix',
            icon: Layers,
            color: '#024DA1',
            requiredModules: ['nutanix'],
            items: [
              { to: '/nutanix/clusters', icon: Layers, label: 'Clusters' },
              { to: '/nutanix/vms', icon: HardDrive, label: 'Virtual Machines' },
            ],
          },
          {
            id: 'proxmox-compute',
            label: 'Proxmox',
            icon: Server,
            color: '#E57000',
            requiredModules: ['proxmox'],
            items: [
              { to: '/proxmox', icon: LayoutDashboard, label: 'Overview' },
              { to: '/proxmox/clusters', icon: Cloud, label: 'Clusters' },
              { to: '/proxmox/vms', icon: HardDrive, label: 'Virtual Machines' },
              { to: '/proxmox/containers', icon: Container, label: 'Containers' },
              { to: '/proxmox/performance', icon: Activity, label: 'Performance' },
              { to: '/proxmox/pools', icon: FolderOpen, label: 'Resource Pools' },
              { to: '/proxmox/tenants', icon: Building2, label: 'Tenants' },
            ],
          },
          {
            id: 'linux-compute',
            label: 'Linux',
            icon: Terminal,
            color: '#FCC624',
            requiredModules: ['servers', 'kvm'],
            items: [
              { to: '/servers', icon: Server, label: 'Linux Servers' },
              { to: '/kvm', icon: HardDrive, label: 'KVM Hypervisor' },
            ],
          },
        ],
      },
      {
        id: 'networking',
        label: 'Networking',
        icon: Network,
        vendors: [
          {
            id: 'vmware-nsx',
            label: 'VMware NSX',
            icon: Network,
            color: '#607D8B',
            requiredModules: ['vmware-nsx'],
            items: [
              { to: '/vmware-nsx/overview', icon: LayoutDashboard, label: 'Overview' },
              { to: '/vmware-nsx/segments', icon: Network, label: 'Segments' },
              { to: '/vmware-nsx/gateways', icon: Router, label: 'Gateways' },
              { to: '/vmware-nsx/security', icon: ShieldCheck, label: 'Security' },
              { to: '/vmware-nsx/firewall', icon: Shield, label: 'Firewall' },
              { to: '/vmware-nsx/load-balancers', icon: Layers, label: 'Load Balancers' },
              { to: '/vmware-nsx/edge-nodes', icon: Server, label: 'Edge Nodes' },
              { to: '/vmware-nsx/federation', icon: Cloud, label: 'Federation' },
              { to: '/vmware-nsx/intelligence', icon: Eye, label: 'Intelligence' },
              { to: '/vmware-nsx/malware-prevention', icon: ShieldCheck, label: 'Malware Prevention' },
            ],
          },
          {
            id: 'vmware-avi',
            label: 'VMware Avi Load Balancer',
            icon: Layers,
            color: '#607D8B',
            requiredModules: ['vmware-avi'],
            items: [
              { to: '/vmware-avi', icon: LayoutDashboard, label: 'Avi Load Balancer' },
              { to: '/vmware-avi/virtual-services', icon: Network, label: 'Virtual Services' },
              { to: '/vmware-avi/pools', icon: Database, label: 'Server Pools' },
              { to: '/vmware-avi/service-engines', icon: Server, label: 'Service Engines' },
              { to: '/vmware-avi/certificates', icon: Key, label: 'Certificates' },
              { to: '/vmware-avi/waf-policies', icon: ShieldCheck, label: 'WAF Policies' },
              { to: '/vmware-avi/gslb-services', icon: Cloud, label: 'GSLB Services' },
            ],
          },
          {
            id: 'vmware-vcd',
            label: 'VMware Cloud Director',
            icon: Cloud,
            color: '#607D8B',
            requiredModules: ['vmware-vcd'],
            items: [
              { to: '/vmware-vcd', icon: LayoutDashboard, label: 'Cloud Director' },
              { to: '/vmware-vcd/organizations', icon: Building2, label: 'Organizations' },
              { to: '/vmware-vcd/catalogs', icon: FolderOpen, label: 'Catalogs' },
              { to: '/vmware-vcd/networks', icon: Network, label: 'Networks' },
              { to: '/vmware-vcd/edge-gateways', icon: Shield, label: 'Edge Gateways' },
            ],
          },
          {
            id: 'vmware-cloud',
            label: 'VMware Cloud',
            icon: Cloud,
            color: '#0091DA',
            requiredModules: ['vmware-cloud'],
            items: [
              { to: '/vmware-cloud', icon: LayoutDashboard, label: 'Cloud Dashboard' },
              { to: '/vmware-cloud/organizations', icon: Building2, label: 'Organizations' },
              { to: '/vmware-cloud/sddcs', icon: Server, label: 'SDDCs' },
              { to: '/vmware-cloud/subscriptions', icon: Package, label: 'Subscriptions' },
            ],
          },
          {
            id: 'vmware-vcf-services',
            label: 'VCF Platform Services',
            icon: Building2,
            color: '#0D7C66',
            requiredModules: ['vmware-vcf-services'],
            items: [
              { to: '/vmware-vcf-services', icon: LayoutDashboard, label: 'Overview' },
              { to: '/vmware-vcf-services/identity', icon: UserCheck, label: 'Identity' },
              { to: '/vmware-vcf-services/databases', icon: Database, label: 'Databases' },
              { to: '/vmware-vcf-services/ai', icon: Cpu, label: 'Private AI' },
              { to: '/vmware-vcf-services/costs', icon: DollarSign, label: 'Costs' },
              { to: '/vmware-vcf-services/orchestrator', icon: Workflow, label: 'Orchestrator' },
            ],
          },
          {
            id: 'cisco',
            label: 'Cisco',
            icon: Router,
            color: '#049FD9',
            requiredModules: ['cisco-switches', 'cisco-routers'],
            items: [
              { to: '/cisco-switches', icon: Network, label: 'Switches' },
              { to: '/cisco-routers', icon: Router, label: 'Routers' },
            ],
          },
          {
            id: 'ubiquiti',
            label: 'Ubiquiti Unifi',
            icon: Wifi,
            color: '#006FFF',
            requiredModules: ['unifi'],
            items: [
              { to: '/unifi', icon: LayoutDashboard, label: 'Overview' },
              { to: '/unifi/controllers', icon: Server, label: 'Controllers' },
              { to: '/unifi/sites', icon: Building2, label: 'Sites' },
              { to: '/unifi/devices', icon: Router, label: 'Devices' },
              { to: '/unifi/clients', icon: Users, label: 'Clients' },
              { to: '/unifi/networks', icon: Network, label: 'Networks' },
              { to: '/unifi/wlans', icon: Wifi, label: 'WLANs' },
            ],
          },
        ],
      },
      {
        id: 'storage',
        label: 'Storage',
        icon: HardDriveDownload,
        vendors: [
          {
            id: 'pure-storage',
            label: 'Pure Storage',
            icon: HardDriveDownload,
            color: '#FE5000',
            requiredModules: ['puredge-storage'],
            items: [
              { to: '/pure-storage', icon: HardDriveDownload, label: 'FlashArray' },
            ],
          },
          {
            id: 'file-storage',
            label: 'File Storage',
            icon: FolderOpen,
            color: '#8B5CF6',
            requiredModules: ['file-shares'],
            items: [
              { to: '/file-shares', icon: FolderOpen, label: 'File Shares' },
            ],
          },
          {
            id: 'vmware-vsan',
            label: 'VMware vSAN',
            icon: HardDrive,
            color: '#607D8B',
            requiredModules: ['vmware-vsan'],
            items: [
              { to: '/vmware-vsan', icon: HardDrive, label: 'Overview' },
              { to: '/vmware-vsan/clusters', icon: Layers, label: 'Clusters' },
              { to: '/vmware-vsan/performance', icon: BarChart3, label: 'Performance' },
              { to: '/vmware-vsan/policies', icon: ShieldCheck, label: 'Policies' },
            ],
          },
        ],
      },
      {
        id: 'containers',
        label: 'Containers',
        icon: Container,
        vendors: [
          {
            id: 'docker',
            label: 'Docker',
            icon: Container,
            color: '#2496ED',
            requiredModules: ['docker'],
            items: [
              { to: '/docker/containers', icon: Container, label: 'Containers' },
              { to: '/docker/all-containers', icon: Container, label: 'All Containers' },
              { to: '/docker/images', icon: Box, label: 'Images' },
            ],
          },
          {
            id: 'kubernetes',
            label: 'Kubernetes',
            icon: Cloud,
            color: '#326CE5',
            requiredModules: ['kubernetes'],
            items: [
              { to: '/kubernetes/clusters', icon: Cloud, label: 'Clusters' },
              { to: '/kubernetes/pods', icon: Box, label: 'Pods' },
              { to: '/kubernetes/deployments', icon: Layers, label: 'Deployments' },
            ],
          },
          {
            id: 'vmware-tanzu',
            label: 'VMware Tanzu',
            icon: Ship,
            color: '#326CE5',
            requiredModules: ['vmware-tanzu'],
            items: [
              { to: '/vmware-tanzu', icon: LayoutDashboard, label: 'Overview' },
              { to: '/vmware-tanzu/supervisors', icon: Server, label: 'Supervisors' },
              { to: '/vmware-tanzu/clusters', icon: Container, label: 'Clusters' },
              { to: '/vmware-tanzu/namespaces', icon: FolderTree, label: 'Namespaces' },
              { to: '/vmware-tanzu/cluster-groups', icon: Layers, label: 'Cluster Groups' },
              { to: '/vmware-tanzu/policies', icon: Shield, label: 'Policies' },
              { to: '/vmware-tanzu/inspections', icon: Search, label: 'Inspections' },
            ],
          },
          {
            id: 'vmware-vsql',
            label: 'VMware vSphere SQL',
            icon: Database,
            color: '#FF9900',
            requiredModules: ['vmware-vsql'],
            items: [
              { to: '/vmware-vsql', icon: LayoutDashboard, label: 'Overview' },
              { to: '/vmware-vsql/history', icon: History, label: 'History' },
              { to: '/vmware-vsql/saved', icon: Save, label: 'Saved Queries' },
              { to: '/vmware-vsql/templates', icon: FileText, label: 'Templates' },
              { to: '/vmware-vsql/schema', icon: Database, label: 'Schema' },
            ],
          },
          {
            id: 'vmware-csp',
            label: 'Cloud Services Platform',
            icon: CloudCog,
            color: '#1A73E8',
            requiredModules: ['vmware-csp'],
            items: [
              { to: '/vmware-csp', icon: LayoutDashboard, label: 'Overview' },
              { to: '/vmware-csp/organizations', icon: Building2, label: 'Organizations' },
              { to: '/vmware-csp/services', icon: Puzzle, label: 'Services' },
              { to: '/vmware-csp/users', icon: Users, label: 'Users & Groups' },
              { to: '/vmware-csp/notifications', icon: Mail, label: 'Notifications' },
              { to: '/vmware-csp/storage', icon: HardDrive, label: 'Storage' },
              { to: '/vmware-csp/tokens', icon: Key, label: 'API Tokens' },
            ],
          },
          {
            id: 'vmware-cloud-dr',
            label: 'Cloud Disaster Recovery',
            icon: ShieldCheck,
            color: '#D32F2F',
            requiredModules: ['vmware-cloud-dr'],
            items: [
              { to: '/vmware-cloud-dr', icon: LayoutDashboard, label: 'Overview' },
              { to: '/vmware-cloud-dr/protected-sites', icon: Shield, label: 'Protected Sites' },
              { to: '/vmware-cloud-dr/protection-groups', icon: Layers, label: 'Protection Groups' },
              { to: '/vmware-cloud-dr/recovery-plans', icon: ClipboardCheck, label: 'Recovery Plans' },
              { to: '/vmware-cloud-dr/snapshots', icon: Archive, label: 'Snapshots' },
              { to: '/vmware-cloud-dr/replications', icon: RefreshCw, label: 'Replications' },
            ],
          },
          {
            id: 'vmware-identity-lifecycle',
            label: 'Identity & Lifecycle',
            icon: KeyRound,
            color: '#7B1FA2',
            requiredModules: ['vmware-identity-lifecycle'],
            items: [
              { to: '/vmware-identity-lifecycle', icon: LayoutDashboard, label: 'Overview' },
              { to: '/vmware-identity-lifecycle/providers', icon: Fingerprint, label: 'Identity Providers' },
              { to: '/vmware-identity-lifecycle/directories', icon: FolderTree, label: 'Directories' },
              { to: '/vmware-identity-lifecycle/users', icon: Users, label: 'Users & Groups' },
              { to: '/vmware-identity-lifecycle/sso-apps', icon: AppWindow, label: 'SSO Apps' },
              { to: '/vmware-identity-lifecycle/environments', icon: Server, label: 'Environments' },
              { to: '/vmware-identity-lifecycle/products', icon: Package, label: 'Products' },
              { to: '/vmware-identity-lifecycle/licenses', icon: Key, label: 'Licenses' },
            ],
          },
          {
            id: 'vmware-log-analytics',
            label: 'Log Analytics',
            icon: FileSearch,
            color: '#F57C00',
            requiredModules: ['vmware-log-analytics'],
            items: [
              { to: '/vmware-log-analytics', icon: LayoutDashboard, label: 'Overview' },
              { to: '/vmware-log-analytics/query', icon: Search, label: 'Query Console' },
              { to: '/vmware-log-analytics/events', icon: Activity, label: 'Events' },
              { to: '/vmware-log-analytics/alerts', icon: AlertCircle, label: 'Alerts' },
              { to: '/vmware-log-analytics/dashboards', icon: Monitor, label: 'Dashboards' },
              { to: '/vmware-log-analytics/sources', icon: Server, label: 'Sources' },
              { to: '/vmware-log-analytics/forwarders', icon: Webhook, label: 'Forwarders' },
            ],
          },
          {
            id: 'lxc',
            label: 'LXC',
            icon: Container,
            color: '#333333',
            requiredModules: ['lxc'],
            items: [
              { to: '/lxc', icon: Container, label: 'Containers' },
            ],
          },
        ],
      },
      {
        id: 'hardware',
        label: 'Hardware (OOB)',
        icon: Cpu,
        vendors: [
          {
            id: 'dell',
            label: 'Dell',
            icon: Cpu,
            color: '#007DB8',
            requiredModules: ['dell-servers'],
            items: [
              { to: '/dell-servers', icon: Cpu, label: 'Servers (iDRAC)' },
            ],
          },
          {
            id: 'hpe',
            label: 'HPE',
            icon: Cpu,
            color: '#01A982',
            requiredModules: ['hp-servers'],
            items: [
              { to: '/hp-servers', icon: Cpu, label: 'Servers (iLO)' },
            ],
          },
          {
            id: 'ipmi',
            label: 'Generic IPMI',
            icon: Cpu,
            color: '#6B7280',
            requiredModules: ['ipmi-bmc'],
            items: [
              { to: '/ipmi-bmc', icon: Cpu, label: 'IPMI / BMC' },
            ],
          },
        ],
      },
      {
        id: 'databases',
        label: 'Databases',
        icon: Database,
        vendors: [
          {
            id: 'postgresql',
            label: 'PostgreSQL',
            icon: Database,
            color: '#336791',
            requiredModules: ['postgresql'],
            items: [
              { to: '/postgresql', icon: Database, label: 'Instances' },
            ],
          },
          {
            id: 'mysql',
            label: 'MySQL',
            icon: Database,
            color: '#4479A1',
            requiredModules: ['mysql'],
            items: [
              { to: '/mysql', icon: Database, label: 'Instances' },
            ],
          },
          {
            id: 'oracle',
            label: 'Oracle',
            icon: Database,
            color: '#F80000',
            requiredModules: ['oracle-db'],
            items: [
              { to: '/oracle-db', icon: Database, label: 'Instances' },
            ],
          },
          {
            id: 'redis',
            label: 'Redis',
            icon: Database,
            color: '#DC382D',
            requiredModules: ['redis'],
            items: [
              { to: '/redis', icon: Database, label: 'Instances' },
            ],
          },
        ],
      },
    ],
  },

  // ==========================================================================
  // END USER COMPUTING
  // ==========================================================================
  {
    id: 'end-user-computing',
    label: 'End User Computing',
    icon: MonitorPlay,
    color: '#8B5CF6',
    subMenus: [
      {
        id: 'vdi',
        label: 'Virtual Desktops',
        icon: MonitorPlay,
        vendors: [
          {
            id: 'vmware-horizon',
            label: 'VMware Horizon',
            icon: MonitorPlay,
            color: '#607D8B',
            requiredModules: ['vmware-horizon'],
            items: [
              { to: '/vmware-horizon/desktop-pools', icon: MonitorPlay, label: 'Desktop Pools' },
              { to: '/vmware-horizon/sessions', icon: Users, label: 'Sessions' },
              { to: '/vmware-horizon/daas', icon: Cloud, label: 'DaaS' },
            ],
          },
          {
            id: 'citrix',
            label: 'Citrix',
            icon: MonitorPlay,
            color: '#452170',
            requiredModules: ['citrix'],
            items: [
              { to: '/citrix', icon: MonitorPlay, label: 'Virtual Apps' },
            ],
          },
        ],
      },
      {
        id: 'app-delivery',
        label: 'Application Delivery',
        icon: AppWindow,
        vendors: [
          {
            id: 'vmware-appvolumes',
            label: 'VMware App Volumes',
            icon: AppWindow,
            color: '#607D8B',
            requiredModules: ['vmware-appvolumes'],
            items: [
              { to: '/vmware-appvolumes', icon: AppWindow, label: 'App Volumes' },
              { to: '/vmware-appvolumes/health', icon: HeartPulse, label: 'Health' },
              { to: '/vmware-appvolumes/activity', icon: Activity, label: 'Activity Log' },
            ],
          },
          {
            id: 'microsoft-appv',
            label: 'Microsoft App-V',
            icon: AppWindow,
            color: '#00A4EF',
            requiredModules: ['microsoft-appv'],
            items: [
              { to: '/microsoft-appv', icon: AppWindow, label: 'App-V' },
            ],
          },
        ],
      },
    ],
  },

  // ==========================================================================
  // IDENTITY & ACCESS
  // ==========================================================================
  {
    id: 'identity-access',
    label: 'Identity & Access',
    icon: Fingerprint,
    color: '#EC4899',
    subMenus: [
      {
        id: 'directory-services',
        label: 'Directory Services',
        icon: Users,
        vendors: [
          {
            id: 'microsoft-ad',
            label: 'Microsoft AD',
            icon: Network,
            color: '#00A4EF',
            requiredModules: ['active-directory'],
            items: [
              { to: '/active-directory/domains', icon: Network, label: 'Domains' },
              { to: '/active-directory/users', icon: UserCog, label: 'Users' },
              { to: '/active-directory/groups', icon: Shield, label: 'Groups' },
              { to: '/active-directory/computers', icon: Monitor, label: 'Computers' },
            ],
          },
        ],
      },
      {
        id: 'identity-management',
        label: 'Identity Management',
        icon: Key,
        vendors: [
          {
            id: 'identity-providers',
            label: 'Identity Providers',
            icon: Key,
            color: '#EC4899',
            requiredModules: ['identity-providers'],
            items: [
              { to: '/identity-providers', icon: Key, label: 'Providers (SAML/OIDC)' },
            ],
          },
          {
            id: 'onboarding',
            label: 'User Onboarding',
            icon: UserPlus,
            color: '#10B981',
            requiredModules: ['onboarding'],
            items: [
              { to: '/identity-management/onboarding/requests', icon: Users, label: 'Onboarding Requests' },
              { to: '/identity-management/onboarding/invitations', icon: Mail, label: 'Invitations' },
            ],
          },
        ],
      },
      {
        id: 'access-governance',
        label: 'Access Governance',
        icon: ShieldCheck,
        vendors: [
          {
            id: 'role-management',
            label: 'Role Management',
            icon: UserCog,
            color: '#F59E0B',
            requiredModules: ['role-templates'],
            items: [
              { to: '/identity-management/role-templates', icon: UserCog, label: 'Role Templates' },
            ],
          },
          {
            id: 'compliance',
            label: 'Compliance',
            icon: ClipboardCheck,
            color: '#10B981',
            requiredModules: ['compliance-profiles'],
            items: [
              { to: '/identity-management/compliance', icon: ClipboardCheck, label: 'Compliance Profiles' },
            ],
          },
        ],
      },
    ],
  },

  // ==========================================================================
  // SECURITY
  // ==========================================================================
  {
    id: 'security',
    label: 'Security',
    icon: Shield,
    color: '#EF4444',
    subMenus: [
      {
        id: 'vulnerability-management',
        label: 'Vulnerability Management',
        icon: Bug,
        vendors: [
          {
            id: 'tenable',
            label: 'Tenable',
            icon: Bug,
            color: '#0096D6',
            requiredModules: ['tenable', 'nessus', 'acas'],
            items: [
              { to: '/tenable', icon: Bug, label: 'Tenable.io' },
              { to: '/nessus', icon: Bug, label: 'Nessus' },
              { to: '/acas', icon: Scan, label: 'ACAS' },
            ],
          },
        ],
      },
      {
        id: 'siem',
        label: 'SIEM & Logging',
        icon: ScrollText,
        vendors: [
          {
            id: 'splunk',
            label: 'Splunk',
            icon: Search,
            color: '#65A637',
            requiredModules: ['splunk'],
            items: [
              { to: '/splunk/instances', icon: Database, label: 'Instances' },
              { to: '/splunk/search', icon: Search, label: 'Search' },
            ],
          },
          {
            id: 'wazuh',
            label: 'Wazuh',
            icon: ShieldCheck,
            color: '#00A9E5',
            requiredModules: ['wazuh'],
            items: [
              { to: '/wazuh', icon: ShieldCheck, label: 'Dashboard' },
            ],
          },
        ],
      },
      {
        id: 'firewall',
        label: 'Firewall',
        icon: Shield,
        vendors: [
          {
            id: 'proxmox-firewall',
            label: 'Proxmox',
            icon: Shield,
            color: '#E57000',
            requiredModules: ['proxmox'],
            items: [
              { to: '/proxmox/firewall', icon: Shield, label: 'Firewall Rules' },
            ],
          },
        ],
      },
    ],
  },

  // ==========================================================================
  // OPERATIONS
  // ==========================================================================
  {
    id: 'operations',
    label: 'Operations',
    icon: Wrench,
    color: '#F59E0B',
    subMenus: [
      {
        id: 'monitoring',
        label: 'Monitoring',
        icon: Activity,
        vendors: [
          {
            id: 'vmware-aria',
            label: 'VMware Aria',
            icon: Activity,
            color: '#607D8B',
            requiredModules: ['vmware-aria-operations', 'vmware-aria-networks', 'vmware-vrni'],
            items: [
              { to: '/vmware-aria-operations', icon: Activity, label: 'Operations' },
              { to: '/vmware-aria-operations/alerts', icon: AlertCircle, label: 'Alerts' },
              { to: '/vmware-aria-operations/dashboards', icon: LayoutDashboard, label: 'Dashboards' },
              { to: '/vmware-aria-operations/vcf', icon: RefreshCw, label: 'VCF Operations' },
              { to: '/vmware-aria-operations/network-ops', icon: Network, label: 'Network Ops' },
              { to: '/vmware-aria-networks', icon: Network, label: 'Aria Networks' },
              { to: '/vmware-vrni', icon: Eye, label: 'vRealize NI' },
            ],
          },
          {
            id: 'vmware-aria-automation',
            label: 'Aria Automation',
            icon: Workflow,
            color: '#7B1FA2',
            requiredModules: ['vmware-aria-automation'],
            items: [
              { to: '/vmware-aria-automation', icon: LayoutDashboard, label: 'Overview' },
              { to: '/vmware-aria-automation/projects', icon: FolderKanban, label: 'Projects' },
              { to: '/vmware-aria-automation/blueprints', icon: FileCode, label: 'Blueprints' },
              { to: '/vmware-aria-automation/catalog', icon: ShoppingBag, label: 'Catalog' },
              { to: '/vmware-aria-automation/deployments', icon: Rocket, label: 'Deployments' },
              { to: '/vmware-aria-automation/pipelines', icon: GitBranch, label: 'Pipelines' },
            ],
          },
          {
            id: 'vmware-vcf-ops',
            label: 'VCF Operations',
            icon: Activity,
            color: '#607D8B',
            requiredModules: ['vmware-vcf-ops'],
            items: [
              { to: '/vmware-vcf-ops', icon: Activity, label: 'Overview' },
              { to: '/vmware-vcf-ops/resources', icon: Server, label: 'Resources' },
              { to: '/vmware-vcf-ops/alerts', icon: AlertCircle, label: 'Alerts' },
              { to: '/vmware-vcf-ops/capacity', icon: BarChart3, label: 'Capacity' },
              { to: '/vmware-vcf-ops/compliance', icon: ShieldCheck, label: 'Compliance' },
              { to: '/vmware-vcf-ops/network', icon: Network, label: 'Network' },
              { to: '/vmware-vcf-ops/logs', icon: ScrollText, label: 'Logs' },
            ],
          },
        ],
      },
      {
        id: 'backup-recovery',
        label: 'Backup & Recovery',
        icon: Archive,
        vendors: [
          {
            id: 'veritas',
            label: 'Veritas',
            icon: Archive,
            color: '#C8102E',
            requiredModules: ['netbackup'],
            items: [
              { to: '/netbackup', icon: Archive, label: 'NetBackup' },
            ],
          },
          {
            id: 'commvault',
            label: 'Commvault',
            icon: Archive,
            color: '#F26522',
            requiredModules: ['commvault'],
            items: [
              { to: '/commvault', icon: Archive, label: 'Backup' },
            ],
          },
          {
            id: 'proxmox-backup',
            label: 'Proxmox',
            icon: Archive,
            color: '#E57000',
            requiredModules: ['proxmox'],
            items: [
              { to: '/proxmox/backups', icon: Archive, label: 'Backups' },
              { to: '/proxmox/dr', icon: LifeBuoy, label: 'Disaster Recovery' },
            ],
          },
          {
            id: 'vmware-srm',
            label: 'VMware SRM',
            icon: LifeBuoy,
            color: '#607D8B',
            requiredModules: ['vmware-srm'],
            items: [
              { to: '/vmware-srm', icon: LifeBuoy, label: 'Overview' },
              { to: '/vmware-srm/pairings', icon: Network, label: 'Site Pairings' },
              { to: '/vmware-srm/protection-groups', icon: ShieldCheck, label: 'Protection Groups' },
              { to: '/vmware-srm/recovery-plans', icon: ClipboardCheck, label: 'Recovery Plans' },
              { to: '/vmware-srm/replications', icon: RefreshCw, label: 'Replications' },
              { to: '/vmware-srm/history', icon: ScrollText, label: 'History' },
            ],
          },
          {
            id: 'vmware-live-recovery',
            label: 'VMware Live Recovery',
            icon: ShieldCheck,
            color: '#607D8B',
            requiredModules: ['vmware-live-recovery'],
            items: [
              { to: '/vmware-live-recovery', icon: ShieldCheck, label: 'Overview' },
              { to: '/vmware-live-recovery/cyber-recovery', icon: Shield, label: 'Cyber Recovery' },
              { to: '/vmware-live-recovery/cyber-recovery/clean-rooms', icon: Box, label: 'Clean Rooms' },
              { to: '/vmware-live-recovery/cyber-recovery/snapshots', icon: HardDriveDownload, label: 'Snapshots' },
              { to: '/vmware-live-recovery/data-protection', icon: Archive, label: 'Data Protection' },
              { to: '/vmware-live-recovery/data-protection/groups', icon: Layers, label: 'Protection Groups' },
              { to: '/vmware-live-recovery/data-protection/jobs', icon: Activity, label: 'Backup Jobs' },
            ],
          },
        ],
      },
      {
        id: 'patching',
        label: 'Patch Management',
        icon: RefreshCw,
        vendors: [
          {
            id: 'microsoft-wsus',
            label: 'Microsoft',
            icon: RefreshCw,
            color: '#00A4EF',
            requiredModules: ['wsus'],
            items: [
              { to: '/wsus', icon: RefreshCw, label: 'WSUS' },
            ],
          },
          {
            id: 'linux-patching',
            label: 'Linux',
            icon: Package,
            color: '#FCC624',
            requiredModules: ['yum'],
            items: [
              { to: '/yum', icon: Package, label: 'YUM / DNF Repos' },
            ],
          },
        ],
      },
      {
        id: 'incidents',
        label: 'Incident Management',
        icon: AlertCircle,
        vendors: [
          {
            id: 'proxmox-incidents',
            label: 'Proxmox',
            icon: AlertCircle,
            color: '#E57000',
            requiredModules: ['proxmox'],
            items: [
              { to: '/proxmox/incidents', icon: AlertCircle, label: 'Incidents' },
            ],
          },
        ],
      },
      {
        id: 'access-management',
        label: 'Access Management',
        icon: Key,
        vendors: [
          {
            id: 'proxmox-access',
            label: 'Proxmox',
            icon: Key,
            color: '#E57000',
            requiredModules: ['proxmox'],
            items: [
              { to: '/proxmox/access/users', icon: Users, label: 'Users' },
              { to: '/proxmox/access/groups', icon: UserCog, label: 'Groups' },
              { to: '/proxmox/access/roles', icon: ShieldCheck, label: 'Roles' },
              { to: '/proxmox/access/acl', icon: Key, label: 'ACLs' },
            ],
          },
        ],
      },
    ],
  },

  // ==========================================================================
  // AI & AUTOMATION
  // ==========================================================================
  {
    id: 'ai-automation',
    label: 'AI & Automation',
    icon: Sparkles,
    color: '#8B5CF6',
    subMenus: [
      {
        id: 'document-ai',
        label: 'Document AI',
        icon: FileText,
        vendors: [
          {
            id: 'document-validation',
            label: 'Document Processing',
            icon: ClipboardCheck,
            color: '#8B5CF6',
            requiredModules: ['document-validation'],
            items: [
              { to: '/admin/document-validation', icon: ClipboardCheck, label: 'Validation Rules' },
              { to: '/admin/document-schemas', icon: FileText, label: 'Document Schemas' },
              { to: '/admin/model-training', icon: Zap, label: 'Model Training' },
              { to: '/admin/review-queue', icon: ClipboardCheck, label: 'Review Queue' },
            ],
          },
        ],
      },
    ],
  },

  // ==========================================================================
  // DEVELOPER
  // ==========================================================================
  {
    id: 'developer',
    label: 'Developer',
    icon: Code,
    color: '#6366F1',
    items: [
      { to: '/developer/architecture', icon: Network, label: 'Architecture' },
      { to: '/developer/api-map', icon: GitBranch, label: 'API Map' },
      { to: '/developer/api-browser', icon: FileCode, label: 'API Browser' },
    ],
  },

  // ==========================================================================
  // ADMINISTRATION
  // ==========================================================================
  {
    id: 'administration',
    label: 'Administration',
    icon: Settings,
    color: '#F59E0B',
    items: [
      { to: '/users', icon: Users, label: 'Users' },
      { to: '/roles', icon: Shield, label: 'Roles & Permissions' },
      { to: '/config/modules', icon: Puzzle, label: 'Modules' },
      { to: '/config/features', icon: Zap, label: 'Feature Flags' },
      { to: '/config/settings', icon: Settings, label: 'Settings' },
      { to: '/config/modules/classification-banners', icon: Shield, label: 'Classification Banners' },
      { to: '/config/modules/logon-banners', icon: FileText, label: 'Logon Banners' },
    ],
  },

  // ==========================================================================
  // PLATFORM
  // ==========================================================================
  {
    id: 'platform',
    label: 'Platform',
    icon: Layers,
    color: '#06B6D4',
    items: [
      { to: '/admin/modules', icon: Puzzle, label: 'Module Management' },
      { to: '/config/licenses', icon: Key, label: 'License Manager' },
      { to: '/admin/fleet', icon: Server, label: 'Fleet Management' },
      { to: '/admin/releases', icon: Package, label: 'Release Management' },
      { to: '/admin/vault', icon: KeyRound, label: 'Vault Management' },
    ],
  },

  // ==========================================================================
  // SYSTEM
  // ==========================================================================
  {
    id: 'system',
    label: 'System',
    icon: Activity,
    color: '#10B981',
    items: [
      { to: '/system/health', icon: Activity, label: 'Health' },
      { to: '/system/logs', icon: Terminal, label: 'Logs' },
    ],
  },

  // ==========================================================================
  // ANALYTICS
  // ==========================================================================
  {
    id: 'analytics',
    label: 'Analytics',
    icon: BarChart3,
    color: '#06B6D4',
    items: [
      { to: '/analytics', icon: BarChart3, label: 'Dashboard' },
      { to: '/analytics/reports', icon: FileText, label: 'Reports' },
    ],
  },

  // ==========================================================================
  // INTEGRATIONS
  // ==========================================================================
  {
    id: 'integrations',
    label: 'Integrations',
    icon: Webhook,
    color: '#EC4899',
    items: [
      { to: '/integrations', icon: Webhook, label: 'Connections' },
      { to: '/integrations/api', icon: Terminal, label: 'API Keys' },
    ],
  },
];

// ============================================================================
// Helper: Filter navigation based on enabled modules
// ============================================================================

function filterNavigationByModules(
  config: TopCategory[],
  enabledModuleIds: Set<string>
): TopCategory[] {
  return config
    .map((category) => {
      // Categories with direct items (no submenus)
      if (category.items && !category.subMenus) {
        return category;
      }

      // Categories with submenus
      if (category.subMenus) {
        const filteredSubMenus = category.subMenus
          .map((subMenu) => {
            const filteredVendors = subMenu.vendors.filter((vendor) => {
              // If no required modules specified, always show
              if (!vendor.requiredModules || vendor.requiredModules.length === 0) {
                return true;
              }
              // Show if ANY of the required modules is enabled
              return vendor.requiredModules.some((modId) => enabledModuleIds.has(modId));
            });

            if (filteredVendors.length === 0) return null;

            return { ...subMenu, vendors: filteredVendors };
          })
          .filter(Boolean) as SubMenu[];

        if (filteredSubMenus.length === 0) return null;

        return { ...category, subMenus: filteredSubMenus };
      }

      return category;
    })
    .filter(Boolean) as TopCategory[];
}

// ============================================================================
// Master-only filtering — hide categories/items not available in instance mode
// ============================================================================

// IDs of categories that are entirely master-only
const MASTER_ONLY_CATEGORIES = new Set<string>(['developer']);

// Route paths that are master-only within a shared category
const MASTER_ONLY_ROUTES = new Set([
  '/admin/modules',    // Module Management — master MMS
  '/config/licenses',  // License Manager — master SMS
  '/admin/fleet',      // Fleet Management — master FMS
  '/admin/releases',   // Release Management — master RMS
  '/admin/vault',      // Vault Management — master only (instance uses avatar menu lock)
  '/analytics',        // Analytics Dashboard — master only
  '/analytics/reports', // Analytics Reports — master only
]);

function filterNavigationByMode(
  config: TopCategory[],
  mode: 'master' | 'instance'
): TopCategory[] {
  if (mode === 'master') return config;

  return config
    .filter((cat) => !MASTER_ONLY_CATEGORIES.has(cat.id))
    .map((cat) => {
      if (!cat.items) return cat;

      const filtered = cat.items.filter((item) => !MASTER_ONLY_ROUTES.has(item.to));
      if (filtered.length === 0) return null;

      // Instance mode: add Architecture Map under System
      if (cat.id === 'system') {
        return { ...cat, items: [...filtered, { to: '/system/architecture', icon: Network, label: 'Architecture' }] };
      }

      return { ...cat, items: filtered };
    })
    .filter(Boolean) as TopCategory[];
}

// ============================================================================
// Component
// ============================================================================

export function AdminRail() {
  const location = useLocation();
  const { isRailCollapsed, toggleRailCollapsed, setActiveModule } = useLayout();

  const [enabledModules, setEnabledModules] = useState<EnabledModule[]>([]);
  const [emsMode, setEmsMode] = useState<'master' | 'instance'>('master');
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set(['overview']));
  const [expandedSubMenus, setExpandedSubMenus] = useState<Set<string>>(new Set());
  const [expandedVendors, setExpandedVendors] = useState<Set<string>>(new Set());

  // Load EMS mode (master vs instance)
  useEffect(() => {
    getSetupMode()
      .then((info) => setEmsMode(info.mode))
      .catch(() => setEmsMode('master')); // default to master if call fails
  }, []);

  // Load enabled modules
  useEffect(() => {
    const loadEnabledModules = async () => {
      try {
        const response = await fetchEnabledModules();
        setEnabledModules(response.modules);
      } catch (err) {
        console.error('Failed to load enabled modules:', err);
        setEnabledModules([]);
      }
    };

    loadEnabledModules();

    const handleModuleToggle = () => loadEnabledModules();
    window.addEventListener('ems:modules:updated', handleModuleToggle);
    return () => window.removeEventListener('ems:modules:updated', handleModuleToggle);
  }, []);

  // Filter navigation based on enabled modules and EMS mode
  const filteredNavigation = useMemo(() => {
    const enabledIds = new Set(enabledModules.map((m) => m.id));
    const byModules = filterNavigationByModules(navigationConfig, enabledIds);
    return filterNavigationByMode(byModules, emsMode);
  }, [enabledModules, emsMode]);

  // Auto-expand based on current route
  useEffect(() => {
    const currentPath = location.pathname;

    for (const category of filteredNavigation) {
      // Check direct items
      if (category.items) {
        const match = category.items.find(
          (item) => currentPath === item.to || currentPath.startsWith(item.to + '/')
        );
        if (match) {
          setActiveModule(category.id);
          setExpandedCategories((prev) => new Set([...prev, category.id]));
          return;
        }
      }

      // Check submenus
      if (category.subMenus) {
        for (const subMenu of category.subMenus) {
          for (const vendor of subMenu.vendors) {
            const match = vendor.items.find(
              (item) => currentPath === item.to || currentPath.startsWith(item.to + '/')
            );
            if (match) {
              setActiveModule(category.id);
              setExpandedCategories((prev) => new Set([...prev, category.id]));
              setExpandedSubMenus((prev) => new Set([...prev, `${category.id}:${subMenu.id}`]));
              setExpandedVendors((prev) => new Set([...prev, `${category.id}:${subMenu.id}:${vendor.id}`]));
              return;
            }
          }
        }
      }
    }
  }, [location.pathname, filteredNavigation, setActiveModule]);

  // Toggle handlers
  const toggleCategory = useCallback((categoryId: string) => {
    setExpandedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(categoryId)) next.delete(categoryId);
      else next.add(categoryId);
      return next;
    });
  }, []);

  const toggleSubMenu = useCallback((categoryId: string, subMenuId: string) => {
    const key = `${categoryId}:${subMenuId}`;
    setExpandedSubMenus((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const toggleVendor = useCallback((categoryId: string, subMenuId: string, vendorId: string) => {
    const key = `${categoryId}:${subMenuId}:${vendorId}`;
    setExpandedVendors((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const handleCommandPalette = useCallback(() => {
    window.dispatchEvent(new CustomEvent('ems:command-palette:open'));
  }, []);

  // Route active checks
  const isRouteActive = (to: string) => {
    if (to === '/') return location.pathname === '/';
    return location.pathname === to || location.pathname.startsWith(to + '/');
  };

  const isCategoryActive = (category: TopCategory) => {
    if (category.items) {
      if (category.items.some((item) => isRouteActive(item.to))) return true;
    }
    if (category.subMenus) {
      return category.subMenus.some((sm) =>
        sm.vendors.some((v) => v.items.some((item) => isRouteActive(item.to)))
      );
    }
    return false;
  };

  const isSubMenuActive = (subMenu: SubMenu) => {
    return subMenu.vendors.some((v) => v.items.some((item) => isRouteActive(item.to)));
  };

  const isVendorActive = (vendor: VendorGroup) => {
    return vendor.items.some((item) => isRouteActive(item.to));
  };

  return (
    <aside
      className={`
        fixed inset-y-0 left-0 z-40
        hidden lg:flex flex-col
        bg-[var(--grok-rail-bg)]
        border-r border-[var(--grok-rail-border)]
        transition-[width] duration-200 ease-out
        ${isRailCollapsed ? 'w-16' : 'w-64'}
      `}
      role="navigation"
      aria-label="Admin navigation"
    >
      {/* Header */}
      <div className="flex flex-col gap-2 p-3">
        <NavLink
          to="/"
          className={`
            flex items-center gap-3 p-3 rounded-lg
            transition-all duration-150
            hover:bg-[var(--grok-hover-bg)]
            focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ems-primary-ring)]
            ${isRailCollapsed ? 'justify-center' : ''}
          `}
          aria-label="Go to Overview"
        >
          <img src="/ems-logo-icon.png" alt="EMS" className="w-9 h-9 flex-shrink-0 object-contain" />
          {!isRailCollapsed && (
            <div>
              <h1 className="text-base font-semibold text-[var(--grok-text-primary)]">EMS</h1>
              <p className="text-xs text-[var(--grok-text-muted)]">Admin Console</p>
            </div>
          )}
        </NavLink>

        <button
          onClick={handleCommandPalette}
          className={`
            flex items-center gap-3 p-3 rounded-lg
            text-[var(--grok-text-secondary)]
            transition-all duration-150
            hover:bg-[var(--grok-hover-bg)] hover:text-[var(--grok-text-primary)]
            focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ems-primary-ring)]
            ${isRailCollapsed ? 'justify-center' : ''}
          `}
          aria-label="Open command palette (Cmd+K)"
          title={isRailCollapsed ? 'Command (Cmd+K)' : undefined}
        >
          <Command size={20} className="flex-shrink-0" />
          {!isRailCollapsed && (
            <>
              <span className="text-sm flex-1 text-left">Command</span>
              <kbd className="text-xs px-1.5 py-0.5 rounded bg-[var(--grok-surface-2)] text-[var(--grok-text-muted)] font-mono">
                K
              </kbd>
            </>
          )}
        </button>
      </div>

      <div className="mx-3 border-t border-[var(--grok-rail-border)]" />

      {/* Navigation */}
      <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
        {filteredNavigation.map((category) => {
          const CategoryIcon = category.icon;
          const isExpanded = expandedCategories.has(category.id);
          const categoryActive = isCategoryActive(category);

          return (
            <div key={category.id} className="mb-1">
              {/* Category Header */}
              {!isRailCollapsed ? (
                <button
                  onClick={() => toggleCategory(category.id)}
                  className={`
                    flex items-center gap-2 w-full p-2 rounded-lg
                    text-left transition-all duration-150
                    ${categoryActive ? 'text-[var(--grok-text-primary)]' : 'text-[var(--grok-text-secondary)] hover:text-[var(--grok-text-primary)]'}
                    hover:bg-[var(--grok-hover-bg)]
                  `}
                >
                  <div
                    className="w-1 h-5 rounded-full transition-colors flex-shrink-0"
                    style={{ backgroundColor: categoryActive ? category.color : 'transparent' }}
                  />
                  <CategoryIcon size={18} className="flex-shrink-0" />
                  <span className="text-sm font-medium flex-1 truncate">{category.label}</span>
                  <ChevronDown
                    size={14}
                    className={`flex-shrink-0 transition-transform duration-200 ${isExpanded ? '' : '-rotate-90'}`}
                  />
                </button>
              ) : (
                <NavLink
                  to={
                    category.items?.[0]?.to ||
                    category.subMenus?.[0]?.vendors?.[0]?.items?.[0]?.to ||
                    '/'
                  }
                  className={({ isActive }) => `
                    flex items-center justify-center p-3 rounded-lg transition-all duration-150
                    ${isActive || categoryActive ? 'bg-[var(--grok-rail-item-active-bg)] text-[var(--grok-text-primary)]' : 'text-[var(--grok-text-secondary)] hover:bg-[var(--grok-hover-bg)] hover:text-[var(--grok-text-primary)]'}
                  `}
                  title={category.label}
                  style={{
                    borderLeft: categoryActive ? `2px solid ${category.color}` : '2px solid transparent',
                  }}
                >
                  <CategoryIcon size={20} />
                </NavLink>
              )}

              {/* Category Content (expanded) */}
              {!isRailCollapsed && isExpanded && (
                <div className="mt-1 ml-3 space-y-0.5">
                  {/* Direct items (no submenus) */}
                  {category.items &&
                    category.items.map((item) => {
                      const ItemIcon = item.icon;
                      return (
                        <NavLink
                          key={item.to}
                          to={item.to}
                          className={({ isActive }) => `
                            flex items-center gap-2 px-3 py-1.5 rounded-md text-sm transition-all duration-150
                            ${isActive ? 'bg-[var(--grok-active-bg)] text-[var(--grok-text-primary)]' : 'text-[var(--grok-text-secondary)] hover:bg-[var(--grok-hover-bg)] hover:text-[var(--grok-text-primary)]'}
                          `}
                        >
                          <ItemIcon size={14} className="flex-shrink-0 opacity-60" />
                          <span className="flex-1 truncate">{item.label}</span>
                        </NavLink>
                      );
                    })}

                  {/* Submenus */}
                  {category.subMenus &&
                    category.subMenus.map((subMenu) => {
                      const SubMenuIcon = subMenu.icon;
                      const subMenuKey = `${category.id}:${subMenu.id}`;
                      const isSubMenuExpanded = expandedSubMenus.has(subMenuKey);
                      const subMenuActive = isSubMenuActive(subMenu);

                      return (
                        <div key={subMenu.id} className="mb-0.5">
                          {/* Submenu Header */}
                          <button
                            onClick={() => toggleSubMenu(category.id, subMenu.id)}
                            className={`
                              flex items-center gap-2 w-full px-2 py-1.5 rounded-md
                              text-left transition-all duration-150
                              ${subMenuActive ? 'text-[var(--grok-text-primary)] bg-[var(--grok-surface-2)]' : 'text-[var(--grok-text-secondary)] hover:text-[var(--grok-text-primary)] hover:bg-[var(--grok-hover-bg)]'}
                            `}
                          >
                            <SubMenuIcon size={14} className="flex-shrink-0 opacity-70" />
                            <span className="text-xs font-medium flex-1 truncate">{subMenu.label}</span>
                            <ChevronRight
                              size={12}
                              className={`flex-shrink-0 opacity-50 transition-transform duration-200 ${isSubMenuExpanded ? 'rotate-90' : ''}`}
                            />
                          </button>

                          {/* Vendors within submenu */}
                          {isSubMenuExpanded && (
                            <div className="mt-0.5 ml-2 space-y-0.5 border-l border-[var(--grok-border)] pl-2">
                              {subMenu.vendors.map((vendor) => {
                                const VendorIcon = vendor.icon;
                                const vendorKey = `${category.id}:${subMenu.id}:${vendor.id}`;
                                const isVendorExpanded = expandedVendors.has(vendorKey);
                                const vendorActive = isVendorActive(vendor);

                                return (
                                  <div key={vendor.id} className="mb-0.5">
                                    {/* Vendor Header */}
                                    <button
                                      onClick={() => toggleVendor(category.id, subMenu.id, vendor.id)}
                                      className={`
                                        flex items-center gap-2 w-full px-2 py-1 rounded-md
                                        text-left transition-all duration-150
                                        ${vendorActive ? 'text-[var(--grok-text-primary)]' : 'text-[var(--grok-text-secondary)] hover:text-[var(--grok-text-primary)] hover:bg-[var(--grok-hover-bg)]'}
                                      `}
                                    >
                                      <div
                                        className="w-2 h-2 rounded-full flex-shrink-0"
                                        style={{ backgroundColor: vendor.color }}
                                      />
                                      <VendorIcon size={12} className="flex-shrink-0 opacity-60" />
                                      <span className="text-xs flex-1 truncate">{vendor.label}</span>
                                      <ChevronRight
                                        size={10}
                                        className={`flex-shrink-0 opacity-40 transition-transform duration-200 ${isVendorExpanded ? 'rotate-90' : ''}`}
                                      />
                                    </button>

                                    {/* Vendor Items */}
                                    {isVendorExpanded && (
                                      <div className="mt-0.5 ml-3 space-y-0.5 border-l border-[var(--grok-border)] pl-2">
                                        {vendor.items.map((item) => {
                                          const ItemIcon = item.icon;
                                          return (
                                            <NavLink
                                              key={item.to}
                                              to={item.to}
                                              className={({ isActive }) => `
                                                flex items-center gap-2 px-2 py-1 rounded text-xs transition-all duration-150
                                                ${isActive ? 'bg-[var(--grok-active-bg)] text-[var(--grok-text-primary)]' : 'text-[var(--grok-text-muted)] hover:bg-[var(--grok-hover-bg)] hover:text-[var(--grok-text-primary)]'}
                                              `}
                                            >
                                              <ItemIcon size={10} className="flex-shrink-0 opacity-50" />
                                              <span className="flex-1 truncate">{item.label}</span>
                                            </NavLink>
                                          );
                                        })}
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    })}
                </div>
              )}
            </div>
          );
        })}
      </nav>

      <div className="mx-3 border-t border-[var(--grok-rail-border)]" />

      {/* Bottom */}
      <div className="p-3 space-y-2">
        <AvatarMenu collapsed={isRailCollapsed} />
        <EmergencyLockButton collapsed={isRailCollapsed} />
        <button
          onClick={toggleRailCollapsed}
          className={`
            flex items-center gap-3 w-full p-3 rounded-lg
            text-[var(--grok-text-secondary)] transition-all duration-150
            hover:bg-[var(--grok-hover-bg)] hover:text-[var(--grok-text-primary)]
            focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ems-primary-ring)]
            ${isRailCollapsed ? 'justify-center' : ''}
          `}
          aria-label={isRailCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {isRailCollapsed ? (
            <ChevronRight size={20} />
          ) : (
            <>
              <ChevronLeft size={20} />
              <span className="text-sm">Collapse</span>
            </>
          )}
        </button>
      </div>
    </aside>
  );
}

export default AdminRail;
